// Player identity for Cringe who stole Christmas — same login flow as Modak Marathon
// (modak_main/src/pages/loginSlice.js). The PHP game read these from $_SESSION.
//
//   ?otp=<otp>[&role=GUEST_USER]  real login through the ExtraMilePlay game-server API
//   ?demo=true                    throw-away demo player (random ids, nothing reported)
//   (no params)                   resume the session saved in sessionStorage
//
// Progress is stored per userId + organizationId + sessionId, so a wrong/shared identity makes
// a new player look like they already finished — never fall back to a fixed dev user.

import axios from 'axios';

const KEY = 'cringe.session';
const DAY = 24 * 60 * 60 * 1000;

const isTrue = (v) => typeof v === 'string' && v.trim().toLowerCase() === 'true';

const uuid4 = () =>
  'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16);
  });

// Captured when the app loads: the launcher's ?otp=…&role=… is on the very first URL, but routes
// (and the redirects between them) can drop the query string before the login runs.
const LOGIN_QUERY = new URLSearchParams(window.location.search);
const query = () => LOGIN_QUERY;

// Only an explicit demo=true/1 is a demo link (a guest link carrying demo=false must not be).
const isDemoLink = (q) => ['true', '1'].includes(String(q.get('demo') || '').toLowerCase());

/** A fresh login is being requested by the URL (otp / demo) — don't trust what's stored. */
export const wantsFreshLogin = () => {
  const q = query();
  return Boolean(q.get('otp') || isDemoLink(q));
};

export function readStoredSession() {
  try {
    const s = JSON.parse(sessionStorage.getItem(KEY));
    if (s && s.userId !== 'dev-user' && s.userId && s.sessionId && s.organizationId && (!s.expiry || s.expiry > Date.now())) return s;
  } catch {
    /* storage unavailable or corrupt */
  }
  return null;
}

export function saveSession(s) {
  try {
    sessionStorage.setItem(KEY, JSON.stringify(s));
  } catch {
    /* the in-memory copy still works for this page load */
  }
}

function demoSession(fromMobileApp) {
  const id = 'demo' + Math.floor(Math.random() * 10000);
  return {
    userId: id,
    sessionId: 'demobypass', // the PHP demo session id (stage 1 ends with the "subscribe" message for it)
    organizationId: 'demo' + Math.floor(Math.random() * 100000),
    role: 'demobypass',
    name: id,
    email: `${id}@gmail.com`,
    orgName: '',
    gameId: null,
    token: '',
    companyLogoUrl: process.env.REACT_APP_EM_LOGO,
    backButtonRedirect: process.env.REACT_APP_BASE_URL,
    fromMobileApp,
    expiry: Date.now() + DAY,
  };
}

async function otpSession(otp, role, fromMobileApp) {
  const loginError = (e) => {
    const d = e.response?.data;
    return new Error((typeof d?.message === 'string' && d.message) || d?.err?.message || e.message || 'Login failed');
  };
  const guest = role === 'GUEST_USER';
  const res = await axios
    .post(
    guest ? process.env.REACT_APP_FIREBASE_GUEST_USER_API : process.env.REACT_APP_FIREBASE_LOGIN_API,
    guest ? { otp, role: 'GUEST_USER' } : { otp },
    {
      headers: {
        'client-secret': process.env.REACT_APP_FIREBASE_CLIENT_SECRET || process.env.REACT_APP_CLIENT_SECRET,
        'Content-Type': 'application/json',
        'Idempotency-Key': uuid4(),
      },
      timeout: 20000,
    }
    )
    .catch((e) => {
      throw loginError(e);
    });
  if (res.data.message !== 'LOGIN SUCCESS') throw new Error('Login failed');
  const u = res.data.data;

  const s = {
    userId: u.userId || u.id,
    sessionId: u.sessionId,
    organizationId: u.organizationId,
    role: guest ? 'GUEST_USER' : 'ORG_USER',
    name: `${u.firstName || ''} ${u.lastName || ''}`.trim(),
    email: u.email || u.employeeId || '',
    orgName: '',
    gameId: u.gameId,
    token: u.token || '',
    companyLogoUrl: process.env.REACT_APP_EM_LOGO,
    backButtonRedirect: process.env.REACT_APP_BASE_URL,
    fromMobileApp,
    otp,
    expiry: Date.now() + DAY,
  };

  // Company logo + where Back should go (same lookup as Modak).
  try {
    const org = await axios.get(`${process.env.REACT_APP_ORG_API}/${s.organizationId}`);
    if (org.data?.message === 'ORGANIZATIONS_FETCHED_SUCCESSFULLY') {
      const o = org.data.organization || {};
      s.orgName = o.name || o.companyName || '';
      if (o.companyLogo) {
        const base = process.env.REACT_APP_S3_SOURCE || '';
        s.companyLogoUrl = `${base.endsWith('/') ? base : `${base}/`}${o.companyLogo}`;
      }
      s.backButtonRedirect = `${process.env.REACT_APP_BASE_URL}/game-detail/${u.gameId}`;
    }
  } catch {
    /* logo/back link are cosmetic — keep the defaults */
  }
  return s;
}

/** Resolve the player for this page load, or throw with a message to show. */
export async function resolveSession() {
  const q = query();
  const fromMobileApp = isTrue(q.get('fromMobileApp') || '');
  const stored = readStoredSession();
  let s;

  if (isDemoLink(q)) {
    // A refresh keeps the same demo player instead of minting a new one (which would restart the game).
    s = stored && String(stored.role).startsWith('demo') ? stored : demoSession(fromMobileApp);
  } else if (q.get('otp')) {
    const otp = q.get('otp');
    try {
      s = await otpSession(otp, q.get('role'), fromMobileApp);
    } catch (e) {
      // Only a reload of this same link may resume its player — never a different (e.g. demo) one.
      if (!stored || stored.otp !== otp) throw e;
      s = stored;
    }
  } else if (stored) {
    s = fromMobileApp && !isTrue(String(stored.fromMobileApp)) ? { ...stored, fromMobileApp: true } : stored;
  } else if (process.env.NODE_ENV !== 'production') {
    s = demoSession(fromMobileApp); // local dev without a launcher: a fresh demo player each session
  } else {
    throw new Error('Please launch Cringe who stole Christmas from ExtraMilePlay.');
  }

  saveSession(s);
  return s;
}
